import Vue from 'vue';
import Completer from '../services/Completer';
import {
  Prompt,
  PromptResult,
  PromptConfig,
  PromptOptionText,
  PromptOptionComponent,
  PromptButton,
  PromptOptionRadio,
  PromptOptionRadioGroup,
} from '../../types';

type TextPrompt = Prompt<PromptOptionText> &
  Partial<Omit<PromptOptionText, 'type'>>;

type RadioPrompt = Prompt<PromptOptionRadioGroup> &
  Partial<Omit<PromptOptionRadioGroup, 'type'>>;

const toPrompt = <O>(obj?: string | Prompt<O>): Prompt<O> => {
  return typeof obj === 'string' ? { title: obj } : obj || {};
};

let $uid = 0;

export class PromptController {
  // Shows a prompt on screen
  async show<O>(text: string | Prompt<O>): Promise<PromptResult<O>> {
    $uid += 1;
    const uid = $uid;
    const completer = new Completer<PromptResult<O>>();

    let prompt: PromptConfig<O>;
    if (typeof text === 'string') {
      prompt = { uid, title: text, completer };
    } else {
      prompt = { ...text, uid, completer };
    }

    this.addPrompt(prompt);

    const result = await completer.promise;

    this.remove(uid);

    return result;
  }

  async input(
    text?: string | TextPrompt,
  ): Promise<PromptResult<PromptOptionText>>;

  async input(
    text?: string,
    initialValue?: string,
  ): Promise<PromptResult<PromptOptionText>>;

  async input(text?: string | TextPrompt, initialValue?: string) {
    if (text == null || typeof text === 'string') {
      return this.show<PromptOptionText>({
        title: text,
        options: [
          {
            type: 'text',
            label: '',
            value: initialValue,
            autofocus: true,
          },
        ],
      });
    } else {
      const prompt = text;
      const options = prompt.options?.slice() || [];
      options.unshift({
        type: 'text',
        label: prompt.label || '',
        value: prompt.value,
        autofocus: prompt.autofocus ?? true,
      });
      return this.show({
        ...prompt,
        options,
      });
    }
  }

  async radio(
    config: RadioPrompt,
  ): Promise<PromptResult<PromptOptionRadioGroup>>;

  async radio(
    text: string,
    items: PromptOptionRadio[] | string[],
    initialValue?: string,
  ): Promise<PromptResult<PromptOptionRadioGroup>>;

  async radio(
    text: string | RadioPrompt,
    items?: PromptOptionRadio[] | string[],
    initialValue?: string,
  ) {
    if (typeof text === 'string') {
      if (!items) {
        throw new Error('no radio items');
      }
      return this.show<PromptOptionRadioGroup>({
        title: text,
        options: [
          {
            type: 'radio-group',
            label: '',
            value: initialValue,
            items,
          },
        ],
      });
    } else {
      const prompt = text;
      if (!prompt.items) {
        throw new Error('no radio items');
      }
      const options = prompt.options?.slice() || [];
      options.unshift({
        type: 'radio-group',
        label: prompt.label || '',
        value: prompt.value,
        row: prompt.row,
        items: prompt.items,
      });
      return this.show({
        ...prompt,
        options,
      });
    }
  }

  // Shows a prompt with a title - 'Are you sure?'
  async youSure(text?: string | Prompt): Promise<PromptResult> {
    const prompt = toPrompt(text);
    return this.show({
      title: 'Are you sure?',
      ...prompt,
    });
  }

  // Shows a `youSure` prompt with a `deleteBtn`
  async delete(text?: string | Prompt): Promise<PromptResult> {
    const prompt = toPrompt(text);
    return this.youSure({
      buttons: [this.cancelBtn, this.deleteBtn],
      ...prompt,
    });
  }

  // Shows a prompt with `okBtn`
  async ok(text?: string | Prompt): Promise<PromptResult> {
    const prompt = toPrompt(text);
    return this.show({
      buttons: [this.okBtn],
      ...prompt,
    });
  }

  async component(
    prompt: Omit<Prompt, 'options'> & Omit<PromptOptionComponent, 'type'>,
  ): Promise<PromptResult> {
    const { component, props, on, ...rest } = prompt;
    const _prompt = rest as Prompt;
    _prompt.options = [{ type: 'component', component, props, on }];
    return this.show(_prompt);
  }

  // The default OK button
  okBtn = Object.freeze({ key: 'ok', title: 'OK', primary: true });

  // The default Cancel button
  cancelBtn = Object.freeze({ key: 'cancel', title: 'Cancel', cancel: true });

  // The default Delete button
  deleteBtn = Object.freeze({ key: 'delete', title: 'Delete', destroy: true });

  // The default buttons if no buttons are set
  get defaultButtons(): PromptButton[] {
    return [this.cancelBtn, this.okBtn];
  }

  get hasPrompts(): boolean {
    return this.store.prompts.length > 0;
  }

  get topPrompt(): PromptConfig | undefined {
    const { prompts } = this.store;
    return prompts[prompts.length - 1];
  }

  private store = Vue.observable({ prompts: Array<PromptConfig>() });

  private addPrompt<O>(prompt: PromptConfig<O>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.store.prompts.push(prompt as any);
  }

  private remove(uid: number) {
    this.store.prompts = this.store.prompts.filter(p => p.uid !== uid);
  }
}

const prompt = new PromptController();

export default prompt;
